import React, { useEffect, useState } from 'react';
import useModalView from '@hooks/useModalView';
import Modal from '@ui-library/modal/custom';
import AccountCredentials from '@components/common/accountCredentials';
import { Flex } from '@ui-library/flex';
import OAuth from '@components/sources/source/cloudSource/cloudSourceAuth/Oauth';
import { Button } from '@ui-library/button';
import { RESOURCE_TYPES } from '@components/common/constants';
import { AccountCredentialsContainer } from './styles';
import { IDestDefinition } from '@stores/destinationDefsList';
import { LargeText } from '@ui-library/typography';

type Account = { id: string; name: string; createdAt: string; metadata: { email: string } };

interface Props {
  lastSavedAccountId: string;
  getAccounts: () => Promise<Account[]>;
  deleteAccount: (id: string, onSuccess: () => void) => Promise<void>;
  onSaveCb: (id: string) => void;
  destinationDef: {
    name: string;
    displayName: string;
    config?: IDestDefinition['config'];
  };
  isDisabled: boolean;
}

export const OAuthModalPopup: React.FC<Props> = ({
  getAccounts,
  lastSavedAccountId,
  deleteAccount,
  destinationDef,
  onSaveCb,
  isDisabled = false,
}: Props) => {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(false);
  const [saveButtonLoading, setSaveButtonLoading] = useState(false);

  const [selectedAccount, setSelectedAccount] = useState(lastSavedAccountId);
  const findSelectedAccount = (accountId: string) => accounts.find((acc) => acc.id === accountId);

  const activeAccount = findSelectedAccount(lastSavedAccountId);
  const [onCompleteActId, setOnCompleteActId] = useState('');
  const anyAccountSelected = !!activeAccount && !!lastSavedAccountId;

  const fetchAccounts = async () => {
    setAccounts(await getAccounts());
  };

  useEffect(() => {
    fetchAccounts();
  }, []);

  const onClickHandler = () => {
    setSelectedAccount(lastSavedAccountId);
    showModal(true);
    fetchAccounts();
  };

  const onDelete = async () => {
    setLoading(true);
    await deleteAccount(selectedAccount, () => {
      setAccounts(accounts.filter((acc) => acc.id !== selectedAccount));
      setSelectedAccount(lastSavedAccountId);

      if (selectedAccount === lastSavedAccountId) {
        onSaveCb('');
        setSelectedAccount('');
      }
    });
    setLoading(false);
  };

  const onSave = async () => {
    onSaveCb(selectedAccount);
    showModal(false);
  };

  /**
   * This function will get called when OAuth is completed
   *
   * @param secretId - The secretId we get after completion of OAuth things from `/success` callback
   * @param next - An identifier to enable `Save` button or `Continue` button
   */
  const onCompleteOAuth = async (secretId: string, next?: boolean) => {
    await fetchAccounts();

    if (secretId && next) {
      setSaveButtonLoading(true);
      setOnCompleteActId(secretId);
    }
  };

  // The intention is to trigger this useEffect only when both accounts and onCompleteActId are changed
  useEffect(() => {
    const completedAct = findSelectedAccount(onCompleteActId);
    if (completedAct) {
      setSelectedAccount(onCompleteActId);
      setOnCompleteActId('');
      setSaveButtonLoading(false);
    }
  }, [accounts, onCompleteActId]);

  const [modal, showModal] = useModalView(
    <Modal
      title="Edit Credentials"
      onClose={() => showModal(false)}
      content={
        <Flex flexDirection="column" alignItems="center">
          <AccountCredentialsContainer>
            <AccountCredentials
              accounts={accounts}
              onSelect={setSelectedAccount}
              selectedAccount={selectedAccount}
              onDelete={onDelete}
              isDeleteInProgress={loading}
              rudderCategory={RESOURCE_TYPES.DESTINATION}
            />
          </AccountCredentialsContainer>
          <Flex justifyContent="center">
            <OAuth
              params={{}}
              role={destinationDef.config?.auth?.role || destinationDef.name}
              onComplete={onCompleteOAuth}
              title={destinationDef.displayName}
              rudderCategory={RESOURCE_TYPES.DESTINATION}
              provider={destinationDef.config?.auth?.provider}
            />
          </Flex>
        </Flex>
      }
      onSave={onSave}
      size="lg"
      saveLabel="Save"
      isSaveDisabled={lastSavedAccountId === selectedAccount}
      loading={saveButtonLoading}
    />,
  );

  const getButtonText = () => (anyAccountSelected ? 'Edit Credentials' : 'Create Account');

  const accountInfo = anyAccountSelected
    ? `You are currently connected to ${activeAccount?.name}`
    : 'No account is connected';

  return (
    <>
      {modal}
      <Flex flexDirection="row" justifyContent="space-between" flexGrow alignItemsCenter>
        <LargeText>{accountInfo}</LargeText>
        <Button disabled={isDisabled} name={getButtonText()} onClick={onClickHandler} />
      </Flex>
    </>
  );
};
